
import { Alert, Box, Button, TextField, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import React from 'react';

interface GenerateDrawProps {
  numMats: number;
  setNumMats: (mats: number) => void;
  scheduledMatches: any[];
  restTimeAnalysis: any;
  onGenerateSchedule: () => void;
  setCurrentStep: (step: number) => void;
  onAddRest: () => void;
  onScheduleReset: () => void;
}


// Helper function to create a message box JSX element
const restAnalysisMessageBox = (restTimeAnalysis: any, onAddRest: () => void, onScheduleReset: () => void) => {
  return (
    <>
      {restTimeAnalysis && restTimeAnalysis.wrestlersWithIssues > 0 && (
        <>
          <br></br>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rest Time Analysis: {restTimeAnalysis.wrestlersWithIssues} wrestlers have insufficient rest between matches
            </Typography>
            <Typography variant="body2">
              {restTimeAnalysis.flaggedMatches.length} matches flagged (minimum 4 matches between bouts required)
            </Typography>
            {restTimeAnalysis.wrestlerIssues.slice(0, 3).map((issue: any, index: number) => (
              <Typography key={index} variant="body2" component="div" sx={{ mt: 0.5 }}>
                • {issue.wrestler}: {issue.issues.length} violation(s)
              </Typography>
            ))}
            {restTimeAnalysis.wrestlerIssues.length > 3 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                ...and {restTimeAnalysis.wrestlerIssues.length - 3} more wrestlers
              </Typography>
            )}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" color="primary">
                Try a Fix
              </Button>
              <Button variant="outlined" size="small" color="secondary" onClick={onAddRest}>
                Add Rests
              </Button>
              <Button variant="outlined" size="small" color="error" onClick={onScheduleReset}>
                Reset
              </Button>
            </Box>
          </Alert>
        </>
      )}
      {restTimeAnalysis && restTimeAnalysis.wrestlersWithIssues === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          ✅ All wrestlers have sufficient rest time between matches (minimum 4 matches between bouts)
        </Alert>
      )}
    </>
  );
};

const handleWarningIcon = (isWarning: boolean) => {
  if (isWarning) {
    return " ⚠️";
  }
  return null;
}

const GenerateDraw: React.FC<GenerateDrawProps> = ({
  numMats,
  setNumMats,
  scheduledMatches,
  restTimeAnalysis,
  onGenerateSchedule,
  setCurrentStep,
  onAddRest,
  onScheduleReset
}) => {

  var isRestful = restTimeAnalysis?.wrestlersWithIssues > 0;
  
  return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 3: Schedule Matches
            </Typography>
            
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Assign bout numbers and mats to all matches. Each wrestler gets a 5-match break between bouts.
            </Typography>
            {scheduledMatches.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  How many mats will be available?
                </Typography>
                <TextField
                  type="number"
                  label="Number of Mats"
                  value={numMats}
                  onChange={(e) => setNumMats(parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 10 }}
                  sx={{ mb: 2, width: 200 }}
                />
                <Box sx={{ mt: 2 }}>
                  <Button 
                    variant="contained" 
                    onClick={onGenerateSchedule}
                    disabled={!numMats || numMats < 1}
                  >
                    Generate Schedule
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Successfully scheduled {scheduledMatches.length} matches across {numMats} mat(s)
                </Alert>

                <Grid container spacing={2}>
                  {Array.from({ length: numMats }, (_, matIndex) => {
                    const matMatches = scheduledMatches.filter(match => match.mat === matIndex + 1);
                    const matGroups = Array.from(new Set(matMatches.map(m => m.weightClass)));
                    
                    return (
                      <Grid item xs={12} md={numMats === 1 ? 12 : numMats === 2 ? 6 : 4} key={matIndex}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                              Mat {matIndex + 1}
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Weight Classes ({matGroups.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {matGroups.map(groupName => (
                                  <Chip 
                                    key={groupName}
                                    label={groupName} 
                                    size="small" 
                                    variant="outlined" 
                                    color="primary"
                                  />
                                ))}
                              </Box>
                            </Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Matches ({matMatches.length}):
                            </Typography>
                            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                              {matMatches.map((scheduledMatch) => {
                                const isFlagged = restTimeAnalysis?.flaggedMatches?.some((match: any) => match.mat === scheduledMatch.mat && match.boutNumber === scheduledMatch.boutNumber) ?? false;
                                
                                return (
                                <Card 
                                  key={scheduledMatch.boutNumber} 
                                  variant="outlined" 
                                  sx={{ 
                                    mb: 2,
                                    border: isFlagged ? '2px solid #f44336' : '1px solid rgba(0, 0, 0, 0.12)',
                                    backgroundColor: isFlagged ? '#ffebee' : 'background.paper'
                                  }}
                                >

                                  {!scheduledMatch.isRestPeriod && scheduledMatch.weightClass !== 'REST PERIOD' && (
                                    <CardContent sx={{ p: 2 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="subtitle2" color={isFlagged ? 'error' : 'primary'}>
                                          Match #{scheduledMatch.boutNumber}
                                          {isFlagged && (
                                            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                              ⚠️ Insufficient Rest
                                            </Typography>
                                          )}
                                        </Typography>
                                        <Chip 
                                          label={`${scheduledMatch.weightClass} Rnd:${scheduledMatch.roundNumber}`} 
                                          size="small" 
                                          variant="outlined" 
                                          color={isFlagged ? 'error' : 'default'}
                                        />
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                                        <Typography variant="body2" sx={{ display: 'inline' }}>
                                          {scheduledMatch.match.wrestler1.firstName} {scheduledMatch.match.wrestler1.lastName} ({scheduledMatch.match.wrestler1.schoolName || 'Unknown'})
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mx: 1, display: 'inline' }}>
                                          vs
                                        </Typography>
                                        <Typography variant="body2" sx={{ display: 'inline' }}>
                                          {scheduledMatch.match.wrestler2.firstName} {scheduledMatch.match.wrestler2.lastName} ({scheduledMatch.match.wrestler2.schoolName || 'Unknown'})
                                        </Typography>
                                      </Box>
                                    </CardContent>
                                  )}
                                  {scheduledMatch.weightClass === 'REST PERIOD' && (
                                    <CardContent sx={{ p: 2, border: '3px dotted #ccc', backgroundColor: 'rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                      <Typography variant="subtitle2">
                                       #{scheduledMatch.boutNumber} Rest Period - 5 Minutes
                                      </Typography>
                                    </CardContent>
                                  )}

                                </Card>
                                );
                              })}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                { restAnalysisMessageBox(restTimeAnalysis, onAddRest, onGenerateSchedule) }
  
                
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={() => setCurrentStep(1)}>
                    Back
                  </Button>
                  <Button 
                    variant="contained" 
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        fontSize: '0.875rem',
                      },
                    }}
                    title={isRestful ? 'At least one wrestler has insufficient rest time between matches' : 'Continue to Step 4 View Brackets'}

                    onClick={() => setCurrentStep(3)}
                  >
                    Continue to View Brackets {handleWarningIcon(isRestful)}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )
}

export default GenerateDraw;